import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../components/Layout';
import { BarList, ErrorState, Kpi, Loading, Select, TextInput } from '../components/ui';
import { api } from '../lib/api';
import { daysAgo, dateShort, money, time, today } from '../lib/format';

interface ExpenseCategory {
  id: string;
  name: string;
}

interface DayBookReport {
  from: string;
  to: string;
  totals: {
    sales: { fnb: number; rooms: number; net: number; gross: number; bills: number };
    expenses: { total: number; count: number };
    net: number;
    cash: { sales: number; expenses: number; net: number };
  };
  byDay: Array<{
    day: string;
    salesFnb: number;
    salesRooms: number;
    salesNet: number;
    bills: number;
    expenses: number;
    expenseCount: number;
    net: number;
  }>;
  salesByMode: Array<{ mode: string; amount: number; count: number }>;
  expensesByMode: Array<{ mode: string; amount: number; count: number }>;
  salesByCategory: Array<{ category: string; quantity: number; revenue: number }>;
  expensesByCategory: Array<{ categoryId: string; category: string; amount: number; count: number }>;
  byStaff: Array<{
    userId: string | null;
    name: string;
    sales: number;
    salesCount: number;
    expenses: number;
    expenseCount: number;
    net: number;
  }>;
  sales: Array<{
    id: string;
    number: string;
    kind: 'FNB' | 'ROOM';
    issuedAt: string;
    guestName: string;
    mode: string;
    total: number;
    issuedBy: string | null;
  }>;
  expenses: Array<{
    id: string;
    number: string;
    expenseDate: string;
    category: string;
    paymentMode: string;
    amount: number;
    vendorName: string | null;
    description: string;
    createdBy: string;
  }>;
}

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  CARD: 'Card',
  UPI: 'UPI',
  ROOM: 'Room charge',
  ONLINE: 'Online',
  BANK_TRANSFER: 'Bank transfer',
  OTHER: 'Other',
};

const QUICK_RANGES: Array<[string, number]> = [
  ['Today', 0],
  ['Last 7 days', 6],
  ['Last 30 days', 29],
];

/**
 * The day book itself - filters, KPIs, and the breakdowns. Reused both by
 * the property-side page (below) and by the platform console's read-only
 * view of one hotel's day book (Platform.tsx), which is why the endpoints
 * are passed in rather than hard-coded: the platform console has no
 * per-property expense-categories endpoint, so it omits categoriesEndpoint
 * and that filter simply doesn't render there.
 */
export function DayBookReportView({
  endpoint,
  categoriesEndpoint,
}: {
  endpoint: string;
  categoriesEndpoint?: string;
}) {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [paymentMode, setPaymentMode] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const categories = useQuery({
    queryKey: ['day-book-categories', categoriesEndpoint],
    queryFn: () => api.get<ExpenseCategory[]>(categoriesEndpoint!),
    enabled: Boolean(categoriesEndpoint),
  });

  const params = new URLSearchParams({ from, to });
  if (paymentMode) params.set('paymentMode', paymentMode);
  if (categoryId) params.set('categoryId', categoryId);

  const report = useQuery({
    queryKey: ['day-book', endpoint, from, to, paymentMode, categoryId],
    queryFn: () => api.get<DayBookReport>(`${endpoint}?${params.toString()}`),
  });

  const setRange = (daysBack: number) => {
    setFrom(daysAgo(daysBack));
    setTo(today());
  };

  return (
    <div className="stack">
      <div className="card">
        <div className="card-body row gap-6" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextInput label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextInput label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <Select
            label="Payment mode"
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(PAYMENT_MODE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          {categoriesEndpoint ? (
            <Select
              label="Expense category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">All</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          ) : null}
          <div className="row gap-6">
            {QUICK_RANGES.map(([label, back]) => (
              <button key={label} className="btn sm" onClick={() => setRange(back)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <>
          <div className="kpis">
            <Kpi
              label="Total sales"
              value={money(report.data!.totals.sales.net)}
              foot={`${report.data!.totals.sales.bills} bill(s)`}
            />
            <Kpi label="F&B" value={money(report.data!.totals.sales.fnb)} tone="blue" />
            <Kpi label="Rooms" value={money(report.data!.totals.sales.rooms)} tone="blue" />
            <Kpi
              label="Expenses"
              value={money(report.data!.totals.expenses.total)}
              foot={`${report.data!.totals.expenses.count} entrie(s)`}
              tone="amber"
            />
            <Kpi
              label="Net"
              value={money(report.data!.totals.net)}
              tone={report.data!.totals.net < 0 ? 'red' : ''}
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h3>PnL reconciliation</h3>
            </div>
            <div className="card-body row gap-16" style={{ flexWrap: 'wrap' }}>
              <div className="stat-line">
                <span className="dim">Total sales (all payment methods)</span>
                <b>{money(report.data!.totals.sales.net)}</b>
              </div>
              <div className="stat-line">
                <span className="dim">Total expenses (all payment methods)</span>
                <b>{money(report.data!.totals.expenses.total)}</b>
              </div>
              <div className="stat-line">
                <span className="dim">Net P&amp;L</span>
                <b style={report.data!.totals.net < 0 ? { color: 'var(--danger)' } : undefined}>
                  {money(report.data!.totals.net)}
                </b>
              </div>
            </div>
          </div>

          {report.data!.byDay.length > 1 ? (
            <div className="card">
              <div className="card-head">
                <h3>By day</h3>
              </div>
              <div className="table-wrap">
                <table className="tbl tbl-stack">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="num">F&amp;B</th>
                      <th className="num">Rooms</th>
                      <th className="num">Bills</th>
                      <th className="num">Expenses</th>
                      <th className="num">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data!.byDay.map((row) => (
                      <tr key={row.day}>
                        <td>{dateShort(row.day)}</td>
                        <td className="num">{money(row.salesFnb)}</td>
                        <td className="num">{money(row.salesRooms)}</td>
                        <td className="num dim">{row.bills}</td>
                        <td className="num">{money(row.expenses)}</td>
                        <td className="num b">{money(row.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <h3>Sales by payment mode</h3>
              </div>
              <div className="card-body">
                <BarList
                  rows={report.data!.salesByMode.map((r) => ({
                    label: `${PAYMENT_MODE_LABELS[r.mode] ?? r.mode} · ${r.count}`,
                    value: r.amount,
                  }))}
                  format={money}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-head">
                <h3>Expenses by payment mode</h3>
              </div>
              <div className="card-body">
                <BarList
                  rows={report.data!.expensesByMode.map((r) => ({
                    label: `${PAYMENT_MODE_LABELS[r.mode] ?? r.mode} · ${r.count}`,
                    value: r.amount,
                  }))}
                  format={money}
                />
              </div>
            </div>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <h3>Sales by category</h3>
              </div>
              <div className="card-body">
                <BarList
                  rows={report.data!.salesByCategory.map((r) => ({
                    label: `${r.category} · ${r.quantity} sold`,
                    value: r.revenue,
                  }))}
                  format={money}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-head">
                <h3>Expenses by category</h3>
              </div>
              <div className="card-body">
                <BarList
                  rows={report.data!.expensesByCategory.map((r) => ({
                    label: `${r.category} · ${r.count}`,
                    value: r.amount,
                  }))}
                  format={money}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>By staff</h3>
            </div>
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th className="num">Sales</th>
                    <th className="num">Expenses</th>
                    <th className="num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data!.byStaff.map((row) => (
                    <tr key={row.userId ?? 'unattributed'}>
                      <td>{row.name}</td>
                      <td className="num">{money(row.sales)}</td>
                      <td className="num">{money(row.expenses)}</td>
                      <td className="num b">{money(row.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Sales</h3>
              <span className="small muted">{report.data!.sales.length} bill(s)</span>
            </div>
            {report.data!.sales.length === 0 ? (
              <div className="card-body small muted">No sales in this period.</div>
            ) : (
              <div className="table-wrap">
                <table className="tbl tbl-stack">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Invoice</th>
                      <th>Type</th>
                      <th>Guest</th>
                      <th>Mode</th>
                      <th>Staff</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data!.sales.map((row) => (
                      <tr key={row.id}>
                        <td className="dim">{time(row.issuedAt)}</td>
                        <td className="mono xs">{row.number}</td>
                        <td>{row.kind === 'FNB' ? 'F&B' : 'Room'}</td>
                        <td>{row.guestName}</td>
                        <td>{PAYMENT_MODE_LABELS[row.mode] ?? row.mode}</td>
                        <td className="dim">{row.issuedBy ?? 'Online / unattributed'}</td>
                        <td className="num b">{money(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Expenses</h3>
              <span className="small muted">{report.data!.expenses.length} entrie(s)</span>
            </div>
            {report.data!.expenses.length === 0 ? (
              <div className="card-body small muted">No expenses in this period.</div>
            ) : (
              <div className="table-wrap">
                <table className="tbl tbl-stack">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Expense</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Vendor</th>
                      <th>Mode</th>
                      <th>Staff</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data!.expenses.map((row) => (
                      <tr key={row.id}>
                        <td className="dim">{dateShort(row.expenseDate)}</td>
                        <td className="mono xs">{row.number}</td>
                        <td>{row.category}</td>
                        <td>{row.description}</td>
                        <td className="dim">{row.vendorName ?? '—'}</td>
                        <td>{PAYMENT_MODE_LABELS[row.paymentMode] ?? row.paymentMode}</td>
                        <td className="dim">{row.createdBy}</td>
                        <td className="num b">{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function DayBookPage() {
  return (
    <div className="stack">
      <PageHeader title="Day Book" subtitle="Sales and expenses together, for reconciling the day" />
      <DayBookReportView
        endpoint="/api/reports/day-book"
        categoriesEndpoint="/api/expense-categories?activeOnly=true"
      />
    </div>
  );
}
